import { useRef, useState } from 'react';
import { fromDateInput } from '../lib/expiry.ts';
import { DOCUMENT_ACCEPT } from '../lib/fileTypes.ts';
import { formatFileSize } from '../admin/fileHistory.ts';
import { buildDocumentGroups, type DocVersionFile } from '../lib/documentVersions.ts';
import { Btn } from './Btn.tsx';
import { DateField } from './DateField.tsx';
import { ErrorMessage } from './ErrorMessage.tsx';

export type ComposerMode = 'reference' | 'replace' | 'new';
export type ComposerAttachment = { file: File; expiresAt: string | null; replacesFileIds: string[]; mode: ComposerMode };
export type ReplyPayload = { body: string; attachments: ComposerAttachment[] };

type Draft = { key: string; file: File; expiry: string; mode: ComposerMode; targets: string[] };

export function ReplyComposer({
  role,
  isDocumentQuestion,
  allowMultipleFiles,
  currentFiles,
  onReply,
  pending = false,
  error = null,
}: {
  role: 'vendor' | 'admin';
  isDocumentQuestion: boolean;
  allowMultipleFiles: boolean;
  currentFiles: DocVersionFile[];
  onReply: (payload: ReplyPayload) => void;
  pending?: boolean;
  error?: Error | null;
}) {
  const [body, setBody] = useState('');
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const seq = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);

  // Current documents that can be replacement targets.
  const targetDocs = buildDocumentGroups(currentFiles).map((g) => ({ id: g.current.id, label: g.current.file_name }));
  const canReplace = isDocumentQuestion && targetDocs.length > 0;
  const defaultMode: ComposerMode = role === 'admin' ? 'reference' : canReplace ? 'replace' : 'new';
  // Single-file questions with an existing document must replace it — offering
  // "New" would let a second file coexist with the one the question caps at one of.
  const hideNewMode = !allowMultipleFiles && targetDocs.length > 0;

  function addFiles(list: FileList | null) {
    if (!list) return;
    const added: Draft[] = Array.from(list).map((file) => {
      seq.current += 1;
      return { key: `f${seq.current}`, file, expiry: '', mode: defaultMode, targets: [] };
    });
    setDrafts((d) => [...d, ...added]);
    if (fileInput.current) fileInput.current.value = '';
  }
  const patch = (key: string, p: Partial<Draft>) => setDrafts((d) => d.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const remove = (key: string) => setDrafts((d) => d.filter((x) => x.key !== key));
  const toggleTarget = (key: string, id: string) =>
    setDrafts((d) => d.map((x) => (x.key !== key ? x : { ...x, targets: x.targets.includes(id) ? x.targets.filter((t) => t !== id) : [...x.targets, id] })));

  // Duplicate-target guard (mirrors the server rule).
  const seen = new Set<string>();
  let dup = false;
  for (const dft of drafts) if (dft.mode === 'replace') for (const t of dft.targets) { if (seen.has(t)) dup = true; seen.add(t); }

  const answerCount = drafts.filter((d) => isDocumentQuestion && (role === 'vendor' || d.mode !== 'reference')).length;
  const replCount = seen.size;

  const canSubmit =
    !pending && !dup &&
    (body.trim().length > 0 || drafts.length > 0) &&
    drafts.every((d) => !(d.mode === 'replace' && d.targets.length === 0));

  function submit() {
    if (!canSubmit) return;
    onReply({
      body: body.trim(),
      attachments: drafts.map((d) => ({
        file: d.file,
        expiresAt: d.expiry ? fromDateInput(d.expiry) : null,
        replacesFileIds: d.mode === 'replace' ? d.targets : [],
        mode: d.mode,
      })),
    });
    setBody('');
    setDrafts([]);
  }

  const modes: ComposerMode[] = role === 'admin'
    ? hideNewMode ? ['reference', 'replace'] : ['reference', 'replace', 'new']
    : hideNewMode ? ['replace'] : ['replace', 'new'];
  const modeLabel: Record<ComposerMode, string> = { reference: 'Reference', replace: 'Replaces', new: 'New' };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <textarea
        className="min-h-[60px] w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4D148C]"
        placeholder="Write a reply…"
        value={body}
        disabled={pending}
        onChange={(e) => setBody(e.target.value)}
      />

      {drafts.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span className="rounded-full border border-[#4D148C]/20 bg-[#F3EBFA] px-2.5 py-0.5 font-semibold text-[#4D148C]">
            {drafts.length} file{drafts.length === 1 ? '' : 's'} attached
          </span>
          {isDocumentQuestion && <span>Replacing {replCount} · {answerCount - replCount < 0 ? 0 : answerCount - replCount} new</span>}
        </div>
      )}

      {dup && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Two files are set to replace the same document. Each document can only be replaced once.
        </div>
      )}

      <div className="mt-2.5 flex flex-col gap-3">
        {drafts.map((d) => (
          <div key={d.key} className="overflow-hidden rounded-[10px] border border-slate-300">
            <div className="flex items-center gap-3 border-b border-[#4D148C]/20 bg-[#F3EBFA] px-3.5 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{d.file.name}</div>
                <div className="text-[11px] text-slate-500">{formatFileSize(d.file.size)}</div>
              </div>
              <button type="button" onClick={() => remove(d.key)} disabled={pending}
                      className="rounded p-1 text-slate-500 hover:bg-white" aria-label="Remove file">✕</button>
            </div>
            {isDocumentQuestion && (
              <div className="flex flex-col gap-3 p-3.5">
                <div className="flex flex-wrap items-end gap-3.5">
                  <div className="min-w-[150px] flex-1">
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">Expiry date</label>
                    <DateField value={d.expiry} onChange={(v) => patch(d.key, { expiry: v })} disabled={pending} ariaLabel="Document expiry date" />
                  </div>
                  <div className="min-w-[150px] flex-1">
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">This file</label>
                    <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
                      {modes.map((m) => (
                        <button key={m} type="button" disabled={pending}
                                onClick={() => patch(d.key, { mode: m, targets: m === 'replace' ? d.targets : [] })}
                                className={`flex-1 rounded-md px-2.5 py-2 text-[13px] font-semibold ${d.mode === m ? 'bg-white text-[#4D148C] shadow-sm' : 'text-slate-600'}`}>
                          {modeLabel[m]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {d.mode === 'replace' && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold text-slate-700">Which documents does this file replace?</div>
                    <div className="flex flex-col gap-2">
                      {targetDocs.map((doc) => {
                        const checked = d.targets.includes(doc.id);
                        return (
                          <label key={doc.id} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 ${checked ? 'border-[#4D148C]/30 bg-[#F3EBFA]/60' : 'border-slate-200'}`}>
                            <input type="checkbox" checked={checked} disabled={pending}
                                   onChange={() => toggleTarget(d.key, doc.id)} className="h-4 w-4 accent-[#4D148C]" />
                            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-700">{doc.label}</span>
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Current</span>
                          </label>
                        );
                      })}
                    </div>
                    {d.targets.length === 0 && (
                      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        Select at least one document this file replaces, or switch it to "New".
                      </div>
                    )}
                  </div>
                )}
                {d.mode === 'new' && (
                  <div className="rounded-lg border border-[#4D148C]/20 bg-[#F3EBFA] px-3 py-2 text-xs text-[#4D148C]">
                    Added as an additional document. Nothing is replaced.
                  </div>
                )}
                {d.mode === 'reference' && (
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                    Sent as a reference example only — it does not become the vendor's document.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <input ref={fileInput} type="file" accept={DOCUMENT_ACCEPT} multiple
             className="hidden" disabled={pending} onChange={(e) => addFiles(e.target.files)} />
      <button type="button" onClick={() => fileInput.current?.click()} disabled={pending}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white py-2.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">
        + Attach {drafts.length > 0 ? 'another file' : 'a document'}
      </button>

      <div className="mt-3.5 flex items-center justify-between gap-3 border-t border-slate-200 pt-3.5">
        <div className="max-w-[330px] text-[11px] text-slate-500">Replaced documents move to version history when you send.</div>
        <Btn size="sm" variant="primary" disabled={!canSubmit} loading={pending} onClick={submit}>Send reply</Btn>
      </div>
      {error && <div className="mt-2"><ErrorMessage error={error} /></div>}
    </div>
  );
}
